import React, { useEffect, useMemo, useRef, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Radio, Users, AlertTriangle, Search, Flag, RefreshCw, Video, VolumeX, Volume2, X, Maximize2, Mic } from "lucide-react";

// Tight polling because frames change every 800ms on the client
const POLL_MS = 1500;
const DETAIL_POLL_MS = 1000;
const ABS = (u) => (u && u.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${u}` : u);

export default function AdminLiveProctor() {
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState("");
  const [live, setLive] = useState([]);
  const [query, setQuery] = useState("");
  const [paused, setPaused] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [focused, setFocused] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get("/admin/test-series").then((r) => {
      const list = r.data || [];
      setTests(list);
      if (list.length > 0 && !selectedTest) setSelectedTest(list[0].id);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLive = async () => {
    if (!selectedTest) return;
    try {
      const { data } = await api.get(`/admin/test-series/${selectedTest}/live-proctor`, { params: { t: Date.now() } });
      setLive(data || []);
      setLastUpdate(new Date());
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    if (!selectedTest || paused) return;
    fetchLive();
    timerRef.current = setInterval(fetchLive, POLL_MS);
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTest, paused]);

  const flag = async (attemptId) => {
    const reason = window.prompt("Reason to flag this student as suspicious?");
    if (!reason) return;
    try {
      await api.post(`/admin/exam-attempts/${attemptId}/proctor-flag?reason=${encodeURIComponent(reason)}`);
      toast.success("Flagged in cheating logs");
    } catch { toast.error("Failed"); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return live;
    return live.filter((s) => (s.user_name || "").toLowerCase().includes(q));
  }, [live, query]);

  const activeCount = live.length;
  const staleCount = live.filter((s) => s.is_stale).length;
  const violationCount = live.filter((s) => (s.violations_count || 0) > 0).length;

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-2xl font-bold inline-flex items-center gap-2">
            <span className="relative inline-flex">
              <Radio className="text-red-500" size={22} />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500 pulse-dot" />
            </span>
            Live Proctor
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Near-live video frames (800ms) + audio clips (5s) from every active student. Click a tile for full-screen with audio.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {lastUpdate && <span>Updated {lastUpdate.toLocaleTimeString()}</span>}
          <button
            onClick={() => setPaused(!paused)}
            className={`px-3 py-1.5 rounded-lg border font-semibold inline-flex items-center gap-1 ${paused ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-emerald-50 border-emerald-300 text-emerald-700"}`}
            data-testid="live-proctor-pause-toggle"
          >
            <RefreshCw size={13} className={paused ? "" : "animate-spin"} /> {paused ? "Paused" : "Live"}
          </button>
        </div>
      </div>

      <div className="mt-5 grid md:grid-cols-[1fr_auto_auto_auto] gap-3 items-center">
        <select
          value={selectedTest}
          onChange={(e) => setSelectedTest(e.target.value)}
          className="px-4 py-2.5 rounded-lg border border-slate-300 bg-white font-semibold text-sm"
          data-testid="live-proctor-test-select"
        >
          <option value="">Select a test</option>
          {tests.map((t) => (
            <option key={t.id} value={t.id}>{t.title}</option>
          ))}
        </select>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-3 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search student..."
            className="pl-9 pr-4 py-2.5 rounded-lg border border-slate-300 text-sm bg-white min-w-[240px]"
            data-testid="live-proctor-search"
          />
        </div>
        <Kpi icon={Users} label="Active" value={activeCount} color="text-brand bg-blue-50" testid="kpi-active" />
        <Kpi icon={AlertTriangle} label="Flagged" value={violationCount} color="text-red-600 bg-red-50" testid="kpi-flagged" />
      </div>

      {staleCount > 0 && (
        <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
          <b>{staleCount}</b> student(s) have no new camera frame in the last 5 seconds — their camera or network may be blocked.
        </div>
      )}

      {!selectedTest ? (
        <div className="mt-8 p-10 rounded-2xl border border-dashed border-slate-300 text-center text-slate-500">
          Select a test above to view the live proctor grid.
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-8 p-10 rounded-2xl border border-dashed border-slate-300 text-center text-slate-500">
          <Video size={32} className="mx-auto text-slate-300" />
          <div className="mt-3">No students are currently taking this exam.</div>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="live-proctor-grid">
          {filtered.map((s) => (
            <StudentTile key={s.attempt_id} s={s} onFlag={flag} onExpand={() => setFocused(s)} />
          ))}
        </div>
      )}

      {focused && (
        <FullScreenViewer
          attemptId={focused.attempt_id}
          initialData={focused}
          onClose={() => setFocused(null)}
          onFlag={flag}
        />
      )}
    </DashboardLayout>
  );
}

function Kpi({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="px-4 py-2.5 rounded-lg border border-slate-200 bg-white flex items-center gap-2.5" data-testid={testid}>
      <div className={`w-8 h-8 rounded-md ${color} flex items-center justify-center`}>
        <Icon size={14} />
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <div className="heading font-black text-lg leading-none">{value}</div>
      </div>
    </div>
  );
}

function StudentTile({ s, onFlag, onExpand }) {
  const violated = (s.violations_count || 0) > 0;
  const imgSrc = s.latest_snapshot_url ? ABS(s.latest_snapshot_url) : "";
  const regPhoto = s.registration_photo_url ? ABS(s.registration_photo_url) : "";
  const borderCls = violated ? "border-red-500" : s.is_stale ? "border-amber-400" : "border-emerald-400";

  return (
    <div className={`rounded-xl overflow-hidden border-2 bg-slate-900 relative ${borderCls}`} data-testid={`proctor-tile-${s.attempt_id}`}>
      <button onClick={onExpand} className="block w-full text-left group" title="Click to expand with audio">
        <div className="aspect-[4/3] bg-slate-800 relative">
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={s.user_name}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.opacity = 0.2; }}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-slate-400">
              <Video size={24} />
            </div>
          )}
          {regPhoto && (
            <div className="absolute bottom-1.5 right-1.5 w-12 h-12 rounded-lg overflow-hidden border-2 border-white shadow-lg bg-slate-200 z-10" title="Registration photo">
              <img src={regPhoto} alt="Reg" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center z-10">
            <Maximize2 size={24} className="text-white" />
          </div>
          {!s.is_stale && (
            <div className="absolute top-1.5 left-1.5 bg-red-600 text-white text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded flex items-center gap-1 z-10">
              <span className="w-1.5 h-1.5 rounded-full bg-white pulse-dot" /> Live
            </div>
          )}
          {s.is_stale && (
            <div className="absolute top-1.5 left-1.5 bg-amber-500 text-slate-900 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded z-10">Stale</div>
          )}
          {violated && (
            <div className="absolute top-1.5 right-1.5 bg-red-600 text-white text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1 z-10">
              <AlertTriangle size={9} /> {s.violations_count}
            </div>
          )}
        </div>
      </button>
      <div className="p-2 bg-slate-900 text-white">
        <div className="font-bold text-xs truncate" title={s.user_name}>{s.user_name || "Student"}</div>
        <div className="flex items-center justify-between mt-1">
          <span className="text-[9px] text-slate-400 font-mono">{s.attempt_id.slice(0, 6)}</span>
          <button
            onClick={(e) => { e.stopPropagation(); onFlag(s.attempt_id); }}
            className="text-[9px] font-black uppercase tracking-wider text-red-400 hover:text-red-300 inline-flex items-center gap-0.5"
            data-testid={`proctor-flag-${s.attempt_id}`}
          >
            <Flag size={9} /> Flag
          </button>
        </div>
      </div>
    </div>
  );
}

function FullScreenViewer({ attemptId, initialData, onClose, onFlag }) {
  const [data, setData] = useState(initialData);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);
  const lastAudioUrlRef = useRef("");

  // Poll latest snapshot+audio every 1s
  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const { data: d } = await api.get(`/admin/exam-attempts/${attemptId}/latest-chunk`, { params: { t: Date.now() } });
        // Also fetch latest audio via the live-proctor entry — we already receive it via initialData refreshes
        // but for the full-screen we re-fetch the attempt-specific image too.
        if (!active) return;
        // Merge: only update fields that came back
        setData((cur) => ({
          ...cur,
          latest_snapshot_url: d?.latest_snapshot_url || cur.latest_snapshot_url,
          latest_audio_url: d?.latest_audio_url || cur.latest_audio_url,
          latest_audio_mime: d?.latest_audio_mime || cur.latest_audio_mime,
          latest_timestamp: d?.latest_timestamp || cur.latest_timestamp,
        }));
      } catch { /* noop */ }
    };
    // We call the test's live-proctor and filter — simpler & cached.
    const pollLive = async () => {
      try {
        // Grab via chunks endpoint that now returns both snapshot + audio (below)
        const { data: d } = await api.get(`/admin/exam-attempts/${attemptId}/live`, { params: { t: Date.now() } });
        if (!active || !d) return;
        setData((cur) => ({ ...cur, ...d }));
      } catch { /* noop */ }
    };
    pollLive();
    const id = setInterval(pollLive, DETAIL_POLL_MS);
    return () => { active = false; clearInterval(id); };
  }, [attemptId]);

  // Play latest audio clip when it changes
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const src = data.latest_audio_url ? ABS(data.latest_audio_url) : "";
    if (src && src !== lastAudioUrlRef.current) {
      lastAudioUrlRef.current = src;
      a.src = src;
      a.muted = muted;
      a.play?.().catch(() => {});
    } else {
      a.muted = muted;
    }
  }, [data.latest_audio_url, muted]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const regPhoto = data.registration_photo_url ? ABS(data.registration_photo_url) : "";
  const imgSrc = data.latest_snapshot_url ? ABS(data.latest_snapshot_url) : "";
  const violated = (data.violations_count || 0) > 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4" data-testid="proctor-fullscreen">
      <div className="w-full max-w-6xl relative">
        <div className="flex items-center justify-between mb-3 text-white">
          <div className="flex items-center gap-3">
            {regPhoto && <img src={regPhoto} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-amber-400" />}
            <div>
              <h2 className="heading text-xl font-black">{data.user_name}</h2>
              <div className="text-xs text-slate-300 font-mono">Attempt {data.attempt_id.slice(0, 8)} · {violated ? `${data.violations_count} violations` : "No violations"}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white" data-testid="proctor-fullscreen-close">
            <X size={20} />
          </button>
        </div>

        <div className="relative rounded-2xl overflow-hidden border-2 border-emerald-500 bg-slate-900 aspect-video">
          {imgSrc ? (
            <img src={imgSrc} alt={data.user_name} className="w-full h-full object-contain bg-black" data-testid="proctor-fullscreen-image" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <Video size={48} className="mx-auto" />
                <div className="mt-2 text-sm">Waiting for first camera frame...</div>
              </div>
            </div>
          )}
          <audio ref={audioRef} autoPlay data-testid="proctor-fullscreen-audio" />
          <div className="absolute top-3 left-3 bg-red-600 text-white text-xs font-black uppercase tracking-wider px-3 py-1 rounded flex items-center gap-2 shadow-lg">
            <span className="w-2 h-2 rounded-full bg-white pulse-dot" /> Live
          </div>
          <div className="absolute top-3 right-3 bg-black/60 text-white text-xs font-semibold px-3 py-1 rounded inline-flex items-center gap-1">
            <Mic size={12} /> Audio {muted ? "OFF" : data.latest_audio_url ? "ON" : "(no clip yet)"}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            onClick={() => setMuted(!muted)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white font-semibold"
            data-testid="proctor-fullscreen-mute-toggle"
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            {muted ? "Unmute" : "Mute"}
          </button>
          <button
            onClick={() => onFlag(attemptId)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 text-white font-semibold"
            data-testid="proctor-fullscreen-flag"
          >
            <Flag size={16} /> Flag as Suspicious
          </button>
        </div>

        <div className="mt-3 text-center text-xs text-slate-400">
          Press <kbd className="px-1.5 py-0.5 rounded bg-white/10 border border-white/20 text-white font-mono">Esc</kbd> to close. Image refreshes every {DETAIL_POLL_MS / 1000}s · Audio plays latest 5-sec clip.
        </div>
      </div>
    </div>
  );
}
